// RigSentinel · PWA update lifecycle hook
//
// Registers the service worker, then:
//
//   1. Detects when a NEW version of the SW is installed (the user already
//      has an older controller running). Exposes `updateAvailable=true` so
//      the `UpdateAvailableBanner` can render its "Reload to update" CTA.
//   2. When the user clicks Reload, posts SKIP_WAITING to the waiting worker.
//      The browser fires `controllerchange`; this hook reloads the tab so
//      the user lands on the fresh bundle. No browser-cache flush needed —
//      the new SW serves fresh `/static/<newhash>` URLs from network.
//   3. Polls for updates every 30 minutes AND on window focus so users
//      who keep the PWA open in a background tab discover new builds
//      without waiting for a manual reload.
//
// Critical: avoids the previous staleness bug by:
//   • NOT relying on cache-version bumps to detect new code (browsers
//     fetch sw.js with `Cache-Control: no-cache` semantically — any byte
//     change ships immediately). The sw.js itself ships the eviction.
//   • NOT auto-reloading on first registration (otherwise first-visit
//     users would loop). Only auto-reloads on `controllerchange`, which
//     only fires AFTER a SW change is activated.

import { useEffect, useRef, useState } from "react";

const UPDATE_POLL_INTERVAL_MS = 30 * 60 * 1000;  // 30 minutes

export function useServiceWorkerUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const waitingRef = useRef(null);
  const reloadingRef = useRef(false);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;

    let registration = null;

    const onUpdateFound = () => {
      const installing = registration && registration.installing;
      if (!installing) return;
      installing.addEventListener("statechange", () => {
        // A new SW has been installed AND the page already has a
        // controlling SW → this is an UPDATE, not a first install.
        if (installing.state === "installed" && navigator.serviceWorker.controller) {
          waitingRef.current = installing;
          setUpdateAvailable(true);
        }
      });
    };

    // controllerchange fires when the new SW takes over (after
    // skipWaiting + clients.claim). We reload exactly once so the page
    // executes against the fresh bundle.
    const onControllerChange = () => {
      if (reloadingRef.current) return;
      reloadingRef.current = true;
      window.location.reload();
    };

    // Register and wire listeners
    const ready = window.addEventListener("load", async () => {
      try {
        registration = await navigator.serviceWorker.register("/sw.js");
        registration.addEventListener("updatefound", onUpdateFound);
        navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

        // If a worker is already waiting at registration time, surface
        // the banner immediately (covers the "user already had a queued
        // update from a previous tab" case).
        if (registration.waiting && navigator.serviceWorker.controller) {
          waitingRef.current = registration.waiting;
          setUpdateAvailable(true);
        }

        // Poll for new SW periodically + on focus.
        const tick = () => registration.update().catch(() => {});
        const intervalId = setInterval(tick, UPDATE_POLL_INTERVAL_MS);
        const onFocus = () => tick();
        window.addEventListener("focus", onFocus);

        // Cleanup on unload
        window.addEventListener("beforeunload", () => {
          clearInterval(intervalId);
          window.removeEventListener("focus", onFocus);
        });
      } catch {
        /* SW registration failed — app still works without offline support */
      }
    });

    return () => {
      if (typeof ready === "function") window.removeEventListener("load", ready);
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      }
    };
  }, []);

  /**
   * Apply the pending update — sends SKIP_WAITING to the waiting worker.
   * The new worker will then activate, fire `controllerchange`, and the
   * tab reloads automatically.
   */
  const applyUpdate = () => {
    const w = waitingRef.current;
    if (w) {
      w.postMessage({ type: "SKIP_WAITING" });
      // If for any reason controllerchange doesn't fire (e.g. user is
      // on the first visit ever and there's no controller yet), force a
      // reload after 1.5s as a safety net.
      setTimeout(() => {
        if (!reloadingRef.current) {
          reloadingRef.current = true;
          window.location.reload();
        }
      }, 1500);
    } else {
      window.location.reload();
    }
  };

  return { updateAvailable, applyUpdate };
}
